import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const STAGES = ["Submitted", "In Review", "Quoted", "Paid", "Closed"];

export default function AdminRequestDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [quote, setQuote] = useState({ amount: "", lead_time: "", notes: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/requests/${id}`);
    setData(r.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    await api.patch(`/admin/requests/${id}`, { status });
    toast.success(`Status updated to ${status}`);
    load();
  };

  const sendQuote = async (e) => {
    e.preventDefault();
    if (!quote.amount || Number(quote.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setSending(true);
    try {
      await api.post(`/admin/requests/${id}/quotes`, {
        amount: Number(quote.amount),
        lead_time: quote.lead_time,
        notes: quote.notes,
      });
      toast.success("Quote sent to client");
      setQuote({ amount: "", lead_time: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="text-zinc-400 py-20 text-center">Loading...</div>;
  const { request: r, quotes } = data;
  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-4 py-2.5 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none transition";

  return (
    <div className="max-w-4xl">
      <Link to="/admin/requests" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-coral mb-6 transition" data-testid="back-to-admin-requests">
        <ArrowLeft size={15} /> Back to requests
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-coral text-xs font-bold uppercase tracking-widest mb-1">{r.category}</p>
          <h1 className="font-serif text-3xl font-bold tracking-tight">{r.title}</h1>
          <p className="text-zinc-500 mt-1">{r.client_name} · {r.client_email}</p>
        </div>
        <StatusBadge status={r.status} testid="admin-detail-status" />
      </div>

      {/* Status control */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-6">
        <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3">Update status</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s) => (
            <button key={s} onClick={() => updateStatus(s)} className={`px-4 py-1.5 rounded-sm text-sm font-semibold transition ${r.status === s ? "bg-coral text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid={`set-status-${s.replace(/\s/g, "-")}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Details */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
          <h3 className="font-serif text-lg font-bold">Details</h3>
          <Detail label="Quantity" value={r.quantity} />
          <Detail label="Target price" value={r.target_price || "—"} />
          <Detail label="Product details" value={r.product_details} />
          {r.notes && <Detail label="Notes" value={r.notes} />}
          {r.attachment_data && (
            <a href={r.attachment_data} download={r.attachment_name} className="inline-flex items-center gap-2 text-coral font-semibold text-sm" data-testid="admin-download-attachment">
              <Paperclip size={15} /> {r.attachment_name}
            </a>
          )}
        </div>

        {/* Create quote */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-serif text-lg font-bold mb-4">Create & send quote</h3>
          <form onSubmit={sendQuote} className="space-y-4" data-testid="quote-form">
            <div>
              <label className="text-sm font-semibold">Quote amount (USD) *</label>
              <input type="number" step="0.01" min="0" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} placeholder="0.00" className={`${inp} mt-1.5`} data-testid="quote-amount" />
            </div>
            <div>
              <label className="text-sm font-semibold">Lead time</label>
              <input value={quote.lead_time} onChange={(e) => setQuote({ ...quote, lead_time: e.target.value })} placeholder="e.g. 4-6 weeks" className={`${inp} mt-1.5`} data-testid="quote-lead-time" />
            </div>
            <div>
              <label className="text-sm font-semibold">Notes to client</label>
              <textarea rows={3} value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} className={`${inp} mt-1.5`} data-testid="quote-notes" />
            </div>
            <button disabled={sending} className="w-full bg-coral hover:bg-coral-hover text-white font-semibold py-3 rounded-sm transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="send-quote">
              <Send size={16} /> {sending ? "Sending..." : "Send Quote"}
            </button>
          </form>
        </div>
      </div>

      {/* Existing quotes */}
      {quotes.length > 0 && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-serif text-lg font-bold mb-4">Quotes sent</h3>
          <div className="space-y-3">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center justify-between border-b border-zinc-100 pb-3" data-testid={`admin-quote-${q.id}`}>
                <div>
                  <span className="font-serif text-xl font-bold">${Number(q.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  <span className="text-sm text-zinc-500 ml-2">{q.lead_time}</span>
                </div>
                <StatusBadge status={q.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{label}</div>
    <div className="text-zinc-800 mt-1 whitespace-pre-wrap text-sm">{value}</div>
  </div>
);
